import React, { useRef, useState, useLayoutEffect, useEffect } from 'react';
import { Modal, Button } from 'semantic-ui-react';
import { GoFormzServices } from '../../../AppSettings';
import { analyticsTrack } from '../../../helpers/AnalyticsUtility';
import { apimCall } from '../../../helpers/api';
import { FieldTypes } from '../../../helpers/constants';
import { getRgba } from '../../../helpers/helpers';
import { useAppSelector } from '../../../hooks/ReduxHooks';
import { useActions } from '../../../hooks/useActions';
import { useWindowDimensions } from '../../../hooks/useWindowDimensions';
import { updateProfileSignature } from '../../../profile/actions';
import { ProfileState, Signature, SigningMethod } from '../../../profile/profile-types';
import SignatureWrapper, { SignatureJsValue } from '../../../signature/SignatureWrapper';
import { getSharedFormImageUrl, updateSessionSignature } from '../../actions';
import { getDimensions, getInitials, GetSignatureValue, MODAL_CONFIGURATION } from '../../esignatureHelper';
import { ESignFieldType, ValueChangedForFieldFunc } from '../../formeditor-types';

import './SignatureV2Modal.scss';

export type ESignatureModalProps = {
  onSignatureChange?: (value: string) => Promise<void>;
  valueUpdated: ValueChangedForFieldFunc;
  onClose: VoidFunc;
  onDoneClick?: VoidFunc;
  /** HACK: to temp support IU profile changes */
  onIuSignatureChange?: (value: SignatureJsValue, signingMethod: SigningMethod, changed?: boolean) => void;
  isPartialIuAuthenticated?: boolean;

  name: string;
  saveLocation: string;
  view?: string;

  fieldType: ESignFieldType;
  formId?: string;
  fieldId?: string;
  updateOnly?: boolean;
  templateName?: string;
  templateId?: string;
  profile?: ProfileState;
  signature?: Signature;
  selectFontMode: boolean;
  setSelectFontMode: (isFontMode: boolean) => void;
};

export default function ESignatureModal(props: ESignatureModalProps) {
  const {
    name,
    valueUpdated,
    onClose,
    onIuSignatureChange,
    isPartialIuAuthenticated,
    formId,
    fieldId,
    fieldType,
    updateOnly,
    saveLocation,
    templateName,
    templateId,
    profile,
    selectFontMode,
    setSelectFontMode,
    signature: userSignature
  } = props;

  const configuration = updateOnly ? MODAL_CONFIGURATION.updateOnly : MODAL_CONFIGURATION[fieldType === FieldTypes.eSignature ? 'signature' : 'initials'];
  const isPublicForm = !!useAppSelector<boolean>((state) => !!state.formeditor.sharedAccessToken);
  const defaultName = useAppSelector((state) =>
    isPublicForm ? state.formeditor.policyInfo?.recipient?.name : isPartialIuAuthenticated ? profile?.independentUser.fullName : state.profile.user.fullName
  );

  // const defaultText = fieldType === FieldTypes.eSignatureInitials ? userSignature?.text || getInitials(defaultName) : userSignature?.text || defaultName;
  const defaultText = userSignature?.text || (fieldType === FieldTypes.eSignatureInitials ? getInitials(defaultName) : defaultName);
  const defaultFont = userSignature?.font || null;
  const defaultData = userSignature?.data ? JSON.parse(userSignature.data) : null;
  const readOnly = userSignature && !updateOnly; //by design, if the user has a saved signature, and this is not update only, then set to read only

  const [updateProfileSignatureAction, getSharedFormImageUrlAction, updateSessionSignatureAction] = useActions([
    updateProfileSignature,
    getSharedFormImageUrl,
    updateSessionSignature
  ]);

  const [saving, setSaving] = useState(false);
  const [selectedSigningMethod, setSelectedSigningMethod] = useState(userSignature?.signingMethod ?? 1);
  const [maxWidth, setMaxWidth] = useState<number>();
  const [maxHeight, setMaxHeight] = useState<number>();
  const [hasSignatureValue, setHasSignatureValue] = useState<boolean>(selectedSigningMethod === 0 ? !!userSignature?.data : true);
  const windowSize = useWindowDimensions();
  const contentWrapperRef = useRef<HTMLDivElement>();
  const legalTextRef = useRef<HTMLDivElement>();

  const signatureRef = useRef<TypeMePlease>();
  const { actualWidth, actualHeight } = getDimensions(fieldType);

  useLayoutEffect(() => {
    // this is a workaround because the Modal doesn't allow refs
    const modalHeaderRef: HTMLDivElement = document.querySelector('.signaturev2-modal .header');
    const modalContentRef: HTMLDivElement = document.querySelector('.signaturev2-modal .content');
    const modalActionsRef: HTMLDivElement = document.querySelector('.signaturev2-modal .actions');

    const contentComputedSize = window.getComputedStyle(modalContentRef);
    let newMaxWidth = windowSize.width - modalContentRef.offsetLeft - parseInt(contentComputedSize.paddingLeft) - parseInt(contentComputedSize.paddingRight);
    let newMaxHeight =
      windowSize.height -
      modalHeaderRef.offsetHeight -
      modalActionsRef.offsetHeight -
      legalTextRef.current.offsetHeight -
      40 -
      (selectedSigningMethod == 1 ? 41 : 0);

    newMaxWidth = Math.max(Math.min(newMaxWidth, 900), 300);
    newMaxHeight = Math.max(Math.min(newMaxHeight, 248), 100);

    setMaxWidth(newMaxWidth);
    setMaxHeight(newMaxHeight);
  }, [windowSize, contentWrapperRef, selectedSigningMethod]);

  useEffect(() => {
    handleOnSignatureChange();
  }, [selectedSigningMethod]);

  const handleOnSignatureChange = () => {
    if (!!signatureRef.current) {
      setHasSignatureValue(signatureRef.current.hasValue());
    }
  };

  const handleDoneButtonClicked = async () => {
    if (isPartialIuAuthenticated) {
      // HACk for IU
      setSaving(true);
      // signature ref is the signature wrapper
      const value = await signatureRef.current.getValue();
      onIuSignatureChange(value, selectedSigningMethod as SigningMethod, signatureRef.current.hasChanged());

      return;
    }

    // TODO - move this out into a utility/ helper file
    let newValue = null;

    const useExistingSignature = userSignature?.signingMethod === selectedSigningMethod && !signatureRef.current.hasChanged();

    if (useExistingSignature) {
      newValue = GetSignatureValue(userSignature.files[0].fileId);
    } else {
      setSaving(true);
      const signatureValue = await signatureRef.current.getValue();
      if (signatureValue) {
        newValue = GetSignatureValue(signatureValue.FileId);

        const newUserSignature = {
          files: [
            {
              fileId: signatureValue.FileId,
              width: actualWidth
            }
          ],
          signingMethod: selectedSigningMethod,
          text: signatureValue.Text,
          font: signatureValue.Font,
          data: signatureValue.Data ? JSON.stringify(signatureValue.Data) : undefined
        };
        if (isPublicForm) {
          updateSessionSignatureAction(newUserSignature, fieldType);
          getSharedFormImageUrlAction(signatureValue.FileId);
        } else {
          await apimCall(
            GoFormzServices.AccountService,
            '/profile/signature',
            'PUT',
            fieldType === FieldTypes.eSignatureInitials ? { signature: newUserSignature, type: 1 } : { signature: newUserSignature, type: 0 }
          );
          analyticsTrack(`eSign ${fieldType === FieldTypes.eSignature ? 'Signature' : 'Initials'} is Saved to an Account`, {
            saveLocation: saveLocation,
            signatureType: selectedSigningMethod === 1 ? 'Text' : 'Draw',
            firstSavedSignature: !readOnly,
            isMobile: false
          });

          updateProfileSignatureAction(newUserSignature, fieldType);
        }
      }
    }
    await valueUpdated({ value: newValue ? JSON.stringify(newValue) : null });

    // TODO - the modal doesn't need to know where it's being used from
    if (saveLocation === 'Form Editor') {
      analyticsTrack('eSign Field Filled', {
        type: fieldType === FieldTypes.eSignature ? 'Signature' : 'Initials',
        modalShown: true,
        view: props.view,
        templateId: templateId,
        templateName: templateName,
        fieldId: fieldId,
        fieldName: name,
        isPublicForm: isPublicForm,
        isMobile: false
      });
    }
    onClose();
    setSaving(false);
  };

  return (
    <Modal open dimmer={{ inverted: true, scrolling: false, className: 'signaturev2-dimmer' }} closeOnDimmerClick={false} className="signaturev2-modal">
      <Modal.Header>
        {name}
        {!readOnly ? (
          <div className="header-tabs">
            <button className={`plain-button header-tab${selectedSigningMethod === 1 ? ' selected' : ''}`} onClick={() => setSelectedSigningMethod(1)}>
              Type
            </button>
            <button
              className={`plain-button header-tab${selectedSigningMethod === 0 ? ' selected' : ''}`}
              onClick={() => {
                setSelectedSigningMethod(0);
              }}
            >
              Draw
            </button>
          </div>
        ) : null}
      </Modal.Header>
      <Modal.Content className="custom-padding">
        <div ref={contentWrapperRef}>
          {contentWrapperRef.current ? (
            <SignatureWrapper
              signingMethod={selectedSigningMethod}
              actualWidth={actualWidth}
              actualHeight={actualHeight}
              maxWidth={maxWidth}
              maxHeight={maxHeight}
              defaultText={defaultText}
              defaultFont={defaultFont}
              defaultData={defaultData}
              formId={formId}
              fieldId={fieldId}
              ref={signatureRef}
              color={getRgba('#000000', 1)}
              showLine={true}
              maxTextLength={fieldType === FieldTypes.eSignatureInitials ? 3 : 100}
              forceUppercase={fieldType === FieldTypes.eSignatureInitials}
              readOnly={readOnly}
              selectFontMode={selectFontMode}
              setSelectFontMode={setSelectFontMode}
              onSignatureChange={handleOnSignatureChange}
              onIuSignatureChange={isPartialIuAuthenticated ? handleOnSignatureChange : null}
            />
          ) : null}
        </div>
        <div className={`legal-jargon${fieldType === FieldTypes.eSignatureInitials ? ' initials' : ''}`} ref={legalTextRef}>
          {configuration.disclosure}
          <a href="https://www.goformz.com/terms" target="_blank">
            Terms of Service
          </a>
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => onClose()} className="cancel">
          Cancel
        </Button>
        <Button
          className="done"
          onClick={handleDoneButtonClicked}
          loading={saving}
          disabled={!hasSignatureValue || (selectFontMode && selectedSigningMethod === 1)}
        >
          {configuration.saveText}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
